// The floating compose bar (VISUAL-DESIGN §6). The box is authoritative while the reader types:
// it is an uncontrolled-in-spirit textarea whose value only the reader and explicit actions
// (send, completion, formatting, draft restore) change — the 2026-09-30 "it ate my letters" bug
// was a box overwritten by a value one render behind.
import { insertPastedTable } from '../features/extras/pasteTable';
import { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { data } from '../../bridge/client';
import { ShellContext } from '../shell/ShellContext';
import { userDisplayName } from '../../core/channelListDerivation';
import * as ComposeFormatting from '../../core/composeFormatting';
import { writeThrough } from './textareaEdit';
import { MESSAGE_ENTRY_ATTRIBUTES } from './messageEntry';
import * as EmojiComposer from '../../core/emojiComposer';
import EmojiTable from '../../core/generated/emojiCharacters.json';
import * as MentionComposer from '../../core/mentionComposer';
import type { SendExtras } from '../../bridge/protocol';
import type { UserRecord } from '../../core/records';
import { Icon, type IconName } from '../../design/icons';
import { t } from '../../i18n/i18n';
import { Avatar } from '../Avatar';
import { useComposerAttachments } from '../features/attachments/ComposerAttachments';
import { useVoiceComposer } from '../features/voice/useVoiceComposer';
import { ComposerEmojiButton } from '../features/emoji/ComposerEmojiButton';
import { SuggestionKeys, useComposeFeatures, type ComposeSuggestion } from '../features/compose/useComposeFeatures';
import { EmojiPicker as EmojiPickerModel } from '../../core/emojiPicker';
import { useComposerSchedule } from '../features/scheduled/useComposerSchedule';
import { ComposePreviewPresentation } from '../../core/composePreviewPresentation';
import { MessageBody, type BodyContext } from './MessageBody';
import { ComposerEdit } from '../../core/composerEdit';
import { useComposerEditRequest } from '../features/compose/composeSignals';

const EMOJI_NAMES = Object.keys(EmojiTable as Record<string, string>);
const DRAFT_DEBOUNCE_MS = 400;
/** The server roster is asked once the typing pauses (the natives wait 150-180 ms). */
const ROSTER_DEBOUNCE_MS = 180;

export interface ComposerProps {
  channelKey: string;
  /** The conversation (and thread root) this box sends into — uploads, commands and forwards need it. */
  channelId: string;
  rootId: string | null;
  initialDraft: string;
  placeholder: string;
  participants: UserRecord[];
  customEmojiNames: string[];
  onSend(message: string, extras: SendExtras): void;
  onDraft(message: string): void;
  /** Saves an edit made in this box (§42); false = the server refused (the caller says so). */
  onEdit(postId: string, message: string): Promise<boolean>;
  onTyping(): void;
  disabledNotice?: string | null;
  banner?: ReactNode;
}

type Suggestion =
  | { kind: 'mention'; active: MentionComposer.ActiveMention; items: Array<{ username: string; label: string; userId: string | null; version: number }> }
  | { kind: 'emoji'; active: EmojiComposer.Active; items: Array<{ name: string; char: string | null }> }
  | ComposeSuggestion;

export function Composer(props: ComposerProps) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [text, setText] = useState(props.initialDraft);
  const [cursor, setCursor] = useState(props.initialDraft.length);
  const [highlight, setHighlight] = useState(0);
  // The Markdown preview (MacComposerControls' eye): the draft drawn by the posted-message renderer.
  const [previewing, setPreviewing] = useState(false);
  const draftTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latest = useRef(text);
  latest.current = text;
  // «Editar» (§42, core/composerEdit): non-null while this box holds a sent message.
  const [edit, setEditState] = useState<ComposerEdit | null>(null);
  const editRef = useRef<ComposerEdit | null>(null);
  const setEdit = (next: ComposerEdit | null) => {
    editRef.current = next;
    setEditState(next);
  };
  const wrap = useRef<HTMLDivElement>(null);
  // Files: 📎, paste, drop on the pane, the tray (features/attachments).
  const attach = useComposerAttachments({ channelKey: props.channelKey, channelId: props.channelId, anchor: wrap, disabled: !!props.disabledNotice });
  // Voice notes: the mic beside send while the box is empty, the strip replacing the field (features/voice).
  const voice = useVoiceComposer({ channelKey: props.channelKey, channelId: props.channelId, disabled: !!props.disabledNotice, onSend: props.onSend });

  // The timeline's bottom inset is the bar's MEASURED height (it grows with the draft), handed to
  // the pane as --composer-space so the last message is never under the floating bar.
  useLayoutEffect(() => {
    const el = wrap.current;
    const pane = el?.parentElement;
    if (!el || !pane) return;
    const apply = () => pane.style.setProperty('--composer-space', `${el.offsetHeight + 16}px`);
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, [props.disabledNotice]);

  // A different conversation: flush the old draft, take the new one.
  useEffect(() => {
    setText(props.initialDraft);
    setCursor(props.initialDraft.length);
    setEdit(null);
    requestAnimationFrame(() => ref.current?.focus());
    return () => {
      const pendingEdit = editRef.current;
      if (pendingEdit) {
        // Leaving mid-edit: the edit is dropped, the draft it put aside is what gets saved.
        if (draftTimer.current) clearTimeout(draftTimer.current);
        draftTimer.current = null;
        props.onDraft(pendingEdit.stashedDraft);
        return;
      }
      if (draftTimer.current) {
        clearTimeout(draftTimer.current);
        draftTimer.current = null;
        props.onDraft(latest.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.channelKey]);

  // Grow with the draft up to ~5 lines, then scroll (the bar's height is MEASURED, never a constant).
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = '0px';
    el.style.height = Math.min(180, el.scrollHeight) + 'px';
  }, [text]);

  const candidates = useMemo(
    () => props.participants.filter((u) => u.deleteAt === 0).map((u) => MentionComposer.candidate(u, userDisplayName)),
    [props.participants],
  );
  // The server's roster for the @query (`mentionRoster`): channel members this page has never seen post, and
  // team members outside the channel — the natives' two tiers. Until it answers, the local candidates stand.
  const shell = useContext(ShellContext);
  const teamId = shell?.team.id ?? '';
  const mentionQuery = MentionComposer.activeMention(text, cursor)?.query ?? null;
  const [roster, setRoster] = useState<{ query: string; inChannel: UserRecord[]; outOfChannel: UserRecord[] }>({ query: '', inChannel: [], outOfChannel: [] });
  useEffect(() => {
    if (mentionQuery === null || !teamId) return;
    let alive = true;
    const id = setTimeout(() => {
      void data.call('mentionRoster', teamId, props.channelId, mentionQuery).then((r) => alive && setRoster({ query: mentionQuery, ...r }), () => undefined);
    }, ROSTER_DEBOUNCE_MS);
    return () => {
      alive = false;
      clearTimeout(id);
    };
  }, [mentionQuery, teamId, props.channelId]);
  const usersByName = useMemo(
    () => new Map([...roster.inChannel, ...roster.outOfChannel, ...props.participants].map((u) => [u.username, u])),
    [props.participants, roster],
  );
  // Slash commands, ~channels, priority, ↑-to-edit (features/compose).
  const compose = useComposeFeatures({ channelId: props.channelId, rootId: props.rootId, text, cursor, textarea: ref, place: (v, c) => place(v, c) });

  const suggestion: Suggestion | null = useMemo(() => {
    const mention = MentionComposer.activeMention(text, cursor);
    if (mention) {
      // The roster answered for an OLDER query still narrows correctly (both tiers re-filter by this query).
      const toCandidate = (u: UserRecord) => MentionComposer.candidate(u, userDisplayName);
      const members = MentionComposer.memberTier(candidates, roster.inChannel.map(toCandidate));
      const row = (c: MentionComposer.Candidate) => ({
        username: c.username,
        label: c.displayName,
        userId: c.userId,
        version: usersByName.get(c.username)?.lastPictureUpdate ?? 0,
      });
      const people = MentionComposer.filterCandidates(members, mention.query, 6).map(row);
      const specials = MentionComposer.matchingSpecials(mention.query).map((s) => ({ username: s, label: '', userId: null, version: 0 }));
      const external = MentionComposer.filterExternalCandidates(members, roster.outOfChannel.map(toCandidate), mention.query).map(row);
      const items = [...people, ...specials, ...external];
      return items.length ? { kind: 'mention', active: mention, items } : null;
    }
    const emoji = EmojiComposer.active(text, cursor);
    if (emoji) {
      const names = EmojiComposer.filter([...props.customEmojiNames, ...EMOJI_NAMES], emoji.query, 8);
      const items = names.map((name) => ({ name, char: (EmojiTable as Record<string, string>)[name] ?? null }));
      return items.length ? { kind: 'emoji', active: emoji, items } : null;
    }
    return cursor < 0 ? null : compose.suggestion(text, cursor);
  }, [text, cursor, candidates, roster, usersByName, props.customEmojiNames, compose.suggestion]);

  // A command HINT row is never highlighted (§36.2): the first takeable row, or none.
  const firstTakeable = suggestion ? SuggestionKeys.start(suggestion) : 0;
  useEffect(() => setHighlight(firstTakeable), [suggestion?.kind, suggestion?.items.length, firstTakeable]);

  const update = (value: string, caret: number) => {
    setText(value);
    setPreviewing((p) => ComposePreviewPresentation.afterTextChange(p, value));
    setCursor(caret);
    if (draftTimer.current) clearTimeout(draftTimer.current);
    // The box holds a sent message while editing; the stored draft is the stash.
    if (editRef.current) return;
    draftTimer.current = setTimeout(() => {
      draftTimer.current = null;
      props.onDraft(value);
    }, DRAFT_DEBOUNCE_MS);
  };

  const place = (value: string, caret: number) => {
    update(value, caret);
    requestAnimationFrame(() => {
      const el = ref.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(caret, caret);
    });
  };

  // Text handed in from outside while this composer is on screen (a share INTO the app, P134):
  // appended to what is already typed, never replacing it.
  const placeRef = useRef(place);
  placeRef.current = place;
  useEffect(() => {
    const onInsert = (e: Event) => {
      const d = (e as CustomEvent<{ channelKey: string; text: string }>).detail;
      if (!d || d.channelKey !== props.channelKey || !d.text) return;
      const cur = latest.current;
      const next = cur.trim() ? cur.replace(/\s+$/, '') + '\n' + d.text : d.text;
      placeRef.current(next, next.length);
    };
    window.addEventListener('satori:compose-insert', onInsert);
    return () => window.removeEventListener('satori:compose-insert', onInsert);
  }, [props.channelKey]);

  const accept = (index: number) => {
    if (!suggestion) return;
    if (suggestion.kind === 'command' || suggestion.kind === 'channel') {
      const applied = compose.accept(suggestion, index, text, cursor);
      return place(applied.text, applied.cursor);
    }
    if (suggestion.kind === 'mention') {
      const item = suggestion.items[index];
      const applied = MentionComposer.apply(text, cursor, suggestion.active, item.username);
      place(applied.text, applied.cursor);
    } else {
      const item = suggestion.items[index];
      // A custom emoji has no character: it stays a `:name:` shortcode on the wire.
      const applied = EmojiComposer.apply(text, cursor, suggestion.active, item.char ?? `:${item.name}: `);
      place(applied.text, applied.cursor);
    }
  };

  // «Editar» from a row or ↑ (features/compose): this box takes the message, the draft is put aside.
  useComposerEditRequest(props.channelId, !!props.rootId, (postId) => {
    void data.call('postMessage', postId).then((message) => {
      if (message === null) return;
      if (!editRef.current && draftTimer.current) {
        // The draft's pending write lands now, before the box changes hands.
        clearTimeout(draftTimer.current);
        draftTimer.current = null;
        props.onDraft(latest.current);
      }
      setEdit(ComposerEdit.begin(editRef.current, latest.current, postId, message));
      setPreviewing(false);
      place(message, message.length);
    });
  });

  const cancelEdit = () => {
    const current = editRef.current;
    if (!current) return;
    setEdit(null);
    place(current.stashedDraft, current.stashedDraft.length);
  };

  const send = () => {
    const editing = editRef.current;
    if (editing) {
      const outcome = ComposerEdit.outcome(editing, text);
      if (outcome.kind === 'keepEditing') return;
      cancelEdit();
      if (outcome.kind === 'save') void props.onEdit(editing.postId, outcome.text);
      return;
    }
    const message = text.trim();
    // A bare attachment is a complete message; a failed upload holds the send (features/attachments).
    const gate = attach.gate(message);
    if (gate === 'empty' || gate === 'blocked') return;
    const extras: SendExtras = {};
    // A command cannot take a file (ChannelViewModel.sendMessage): with files, "/x" is a message.
    const withFiles = attach.hasFiles();
    if (withFiles || !compose.trySend(message, extras)) {
      props.onSend(message, { ...extras, ...compose.extras(), ...(withFiles ? { fileIds: attach.take() } : {}) });
      compose.sent();
    }
    if (draftTimer.current) clearTimeout(draftTimer.current);
    draftTimer.current = null;
    props.onDraft('');
    place('', 0);
  };

  // «Programar» (shared/API.md §35): the send button's right-click / long-press. The box and the
  // tray are emptied only once the server accepted (features/scheduled).
  const schedule = useComposerSchedule({
    channelId: props.channelId,
    rootId: props.rootId,
    canSchedule: () => {
      if (editRef.current) return false;
      const g = attach.gate(latest.current.trim());
      return g !== 'empty' && g !== 'blocked' && !latest.current.trim().startsWith('/');
    },
    collect: () => ({ message: latest.current.trim(), fileIds: attach.peek() }),
    onScheduled: () => {
      attach.take();
      if (draftTimer.current) clearTimeout(draftTimer.current);
      draftTimer.current = null;
      props.onDraft('');
      place('', 0);
    },
  });

  // The emoji picker's pick, at the caret (replacing a selection), through place() — the box's
  // own value and selection are read at the moment of the pick, never a render-old copy.
  const insertEmoji = (name: string, custom: boolean) => {
    const el = ref.current;
    const value = el ? el.value : latest.current;
    const start = el ? el.selectionStart : value.length;
    const end = el ? el.selectionEnd : value.length;
    const applied = EmojiPickerModel.insertAtCaret(value, start, end, name, custom);
    place(applied.text, applied.caret);
  };

  const format = (f: ComposeFormatting.Format) => {
    const el = ref.current;
    if (!el) return;
    const applied = ComposeFormatting.toggle(text, el.selectionStart, el.selectionEnd, f);
    update(applied.text, applied.selectionEnd);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(applied.selectionStart, applied.selectionEnd);
    });
  };

  // «Lista con viñetas»: "- " on the caret's / selected lines, a toggle like the formats.
  const bullets = () => {
    const el = ref.current;
    if (!el) return;
    writeThrough(el, ComposeFormatting.toggleBullets(el.value, el.selectionStart, el.selectionEnd));
  };

  if (props.disabledNotice) {
    return (
      <div className="composer-wrap" ref={wrap}>
        <div className="read-only-notice">{props.disabledNotice}</div>
      </div>
    );
  }

  const previewCtx: BodyContext = {
    knownUsernames: new Set(props.participants.map((u) => u.username)),
    currentUsername: shell?.me.username ?? null,
    customEmoji: {},
  };

  const sendGate = attach.gate(text);
  const ready = sendGate === 'ready' || sendGate === 'queued';
  const tools: Array<[ComposeFormatting.Format, IconName, string]> = [
    ['bold', 'bold', t('channel.format.bold')],
    ['italic', 'italic', t('channel.format.italic')],
    ['strikethrough', 'strike', t('channel.format.strikethrough')],
    ['code', 'code', t('channel.format.code')],
  ];

  return (
    <div className="composer-wrap" ref={wrap}>
      {props.banner}
      {edit ? (
        <div className="composer-edit-banner" data-testid="composer-edit-banner">
          <Icon name="pencilLine" size={14} />
          <span className="composer-edit-title">{t('web.compose.editing')}</span>
          <span className="composer-edit-hint">{t('web.compose.editingHint')}</span>
          <button type="button" className="composer-edit-cancel" onClick={cancelEdit} aria-label={t('web.compose.cancelEdit')} title={t('web.compose.cancelEdit')} data-testid="composer-edit-cancel">
            <Icon name="x" size={14} />
          </button>
        </div>
      ) : null}
      {schedule.strip}
      {schedule.sheets}
      {voice.notice}
      {compose.above}
      {suggestion ? (
        <div className="autocomplete" role="listbox">
          {suggestion.kind === 'command' || suggestion.kind === 'channel'
            ? compose.renderItems(suggestion, highlight, accept)
            : suggestion.kind === 'mention'
            ? suggestion.items.map((item, i) => (
                <button key={item.username} className="item" role="option" aria-selected={i === highlight} onMouseDown={(e) => (e.preventDefault(), accept(i))}>
                  {item.userId ? <Avatar userId={item.userId} name={item.label || item.username} size={22} version={item.version} /> : <Icon name="at" size={18} />}
                  <span>{item.label || '@' + item.username}</span>
                  <span className="sub">@{item.username}</span>
                </button>
              ))
            : suggestion.items.map((item, i) => (
                <button key={item.name} className="item" role="option" aria-selected={i === highlight} onMouseDown={(e) => (e.preventDefault(), accept(i))}>
                  <span style={{ width: 22, textAlign: 'center', fontSize: 17 }}>{item.char ?? '·'}</span>
                  <span>:{item.name}:</span>
                </button>
              ))}
        </div>
      ) : null}
      <div className={`composer${voice.recording ? ' voice-recording' : ''}`}>
        {attach.tray}
        {voice.strip}
        {compose.fieldLine}
        {previewing ? (
          <div className="compose-preview" aria-label={t('channel.preview.toggle')} onDoubleClick={() => setPreviewing(false)}>
            <MessageBody message={text} ctx={previewCtx} />
          </div>
        ) : null}
        <textarea
          hidden={previewing}
          ref={ref}
          rows={1}
          value={text}
          placeholder={props.placeholder}
          aria-label={t('channel.field.message')}
          onPaste={(e) => {
            attach.onPaste(e);
            insertPastedTable(e); // a spreadsheet / web table → Markdown (PARITY P301)
          }}
          {...MESSAGE_ENTRY_ATTRIBUTES}
          onChange={(e) => {
            update(e.target.value, e.target.selectionStart);
            props.onTyping();
          }}
          onSelect={(e) => setCursor((e.target as HTMLTextAreaElement).selectionStart)}
          onKeyDown={(e) => {
            if (suggestion) {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setHighlight((h) => SuggestionKeys.move(suggestion, h, 1));
                return;
              }
              if (e.key === 'ArrowUp') {
                e.preventDefault();
                setHighlight((h) => SuggestionKeys.move(suggestion, h, -1));
                return;
              }
              if (e.key === 'Enter' || e.key === 'Tab') {
                // Only hint rows (an argument being typed): Tab does nothing, Enter sends as usual.
                if (SuggestionKeys.canTake(suggestion, highlight)) {
                  e.preventDefault();
                  accept(highlight);
                  return;
                }
                if (e.key === 'Tab') {
                  e.preventDefault();
                  return;
                }
              }
              if (e.key === 'Escape') {
                e.stopPropagation(); // dismissing the strip must not also close the thread pane
                setCursor(-1);
                return;
              }
            }
            if (e.key === 'Escape' && editRef.current) {
              e.preventDefault();
              e.stopPropagation(); // cancel the edit, not the thread pane around it
              cancelEdit();
              return;
            }
            if (!suggestion && !editRef.current && compose.keyDown(e, text)) return;
            // Enter sends, Shift+Enter / Ctrl+Enter break the line (and continue a bulleted list —
            // Windows tester 2026-10-14 tried Ctrl); an IME composition (dead keys, Japanese)
            // owns Enter until it commits — the Mac's accent bug, avoided at the source.
            if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
              if (e.shiftKey || e.ctrlKey) {
                const el = e.currentTarget;
                const applied = ComposeFormatting.newline(el.value, el.selectionStart, el.selectionEnd);
                const plain = el.value.slice(0, el.selectionStart) + '\n' + el.value.slice(el.selectionEnd);
                // A plain Shift+Enter stays the textarea's own (its undo keeps the break).
                if (e.shiftKey && applied.text === plain) return;
                e.preventDefault();
                writeThrough(el, applied);
                return;
              }
              e.preventDefault();
              send();
              return;
            }
            if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey) {
              const k = e.key.toLowerCase();
              if (k === 'b') (e.preventDefault(), format('bold'));
              else if (k === 'i') (e.preventDefault(), format('italic'));
            }
          }}
        />
        <div className="composer-tools">
          {attach.button}
          {tools.map(([f, icon, label]) => (
            <button key={f} className="icon-button" title={label} aria-label={label} onMouseDown={(e) => e.preventDefault()} onClick={() => format(f)}>
              <Icon name={icon} size={16} />
            </button>
          ))}
          <button className="icon-button" title={t('channel.format.bulletList')} aria-label={t('channel.format.bulletList')} onMouseDown={(e) => e.preventDefault()} onClick={bullets}>
            <Icon name="listBullet" size={16} />
          </button>
          <button
            className={`icon-button${previewing ? ' active' : ''}`}
            title={t('channel.preview.toggle')}
            aria-label={t('channel.preview.toggle')}
            aria-pressed={previewing}
            disabled={!previewing && !ComposePreviewPresentation.isAvailable(text)}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              const next = ComposePreviewPresentation.toggle(previewing, text);
              setPreviewing(next);
              if (!next) requestAnimationFrame(() => ref.current?.focus());
            }}
          >
            <Icon name="eye" size={16} />
          </button>
          <ComposerEmojiButton onPick={insertEmoji} onClosed={() => ref.current?.focus()} />
          {compose.toolbar}
          <span className="spacer" />
          {voice.micButton(!text.trim() && !attach.hasFiles())}
          <button className={`send-button${ready ? ' ready' : ''}`} disabled={!ready} onClick={() => schedule.swallowClick() || send()} {...schedule.sendButtonProps} title={attach.sendHint(sendGate) ?? t('mac.send')} aria-label={t('mac.send')}>
            <Icon name="send" size={17} />
          </button>
        </div>
      </div>
    </div>
  );
}
